import type { ReactNode } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../form';
import { DateTimeInput, type DateTimeInputProps } from '../date-time-input';
import { Text } from '@folio/components/ui/text';

export type FormDateTimeInputProps<TFields extends FieldValues> = {
  control: Control<TFields>;
  label?: ReactNode;
  name: Path<TFields>;
  placeholder?: string;
  description?: ReactNode;
  required?: boolean;
} & Omit<DateTimeInputProps, 'label'>;

export const FormDateTimeInput = <TFields extends FieldValues>({
  control,
  label,
  name,
  placeholder,
  description,
  required,
  ...props
}: FormDateTimeInputProps<TFields>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem>
            {!!label && (
              <FormLabel fieldType="date">
                {label}{' '}
                {required && (
                  <Text inline tone="destructive">
                    *
                  </Text>
                )}
              </FormLabel>
            )}
            <FormControl>
              <DateTimeInput
                placeholder={placeholder}
                value={field.value}
                onChange={field.onChange}
                {...props}
              />
            </FormControl>
            {!!description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
